import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";

import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { authPalette } from "../../theme/authPalette";

import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";

import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import { i18n } from "../../translate/i18n";
import { getRegistrationStatus } from "../../services/settings.api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getVersionText } from "../../config/version";
import { useWebInfo } from "../../context/WebInfoContext";

/* =========================
   COPYRIGHT (estilo del nuevo diseño)
========================= */
const Copyright = () => {
  const { webInfo } = useWebInfo();
  // `ap` de useStyles no alcanza aqui: este componente resuelve la suya.
  const ap = authPalette(useTheme().mode);
  const companyName = webInfo?.companyName || "watoolx.com";

  return (
    <Typography variant="body2" align="center" style={{ color: ap.textMuted, fontSize: 13 }}>
      © {new Date().getFullYear()}{" "}
      <Link href="#" style={{ color: ap.accent, fontWeight: 600 }}>
        {companyName}
      </Link>
      .
      <br />
      <span
        style={{
          marginTop: 6,
          display: "inline-block",
          background: ap.chipBackground,
          color: "#fff",
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {getVersionText()}
      </span>
    </Typography>
  );
};

const useStyles = makeStyles((theme) => {
  // 0390 — colores de las pantallas de acceso, resueltos por modo
  const ap = authPalette(theme.mode);

  return {
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "1fr",
    background:
      ap.pageBackground,
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    overflowX: "hidden",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    [theme.breakpoints.down("sm")]: {
      minHeight: "100dvh",
    },
  },

  left: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: theme.spacing(3),
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      alignItems: "stretch",
      padding: 0,
      margin: 0,
      width: "100%",
      minHeight: "100dvh",
      flex: 1,
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(6),
    },
  },

  formPaper: {
    width: "100%",
    maxWidth: 460,
    borderRadius: 18,
    padding: theme.spacing(4),
    boxShadow: ap.cardShadow,
    border: ap.cardBorder,
    background: ap.cardBackground,
    backdropFilter: "blur(10px)",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      width: "100%",
      minHeight: "100dvh",
      borderRadius: 0,
      boxShadow: "none",
      border: "none",
      padding: theme.spacing(2, 2.5),
      paddingTop: "max(16px, env(safe-area-inset-top, 0px))",
      paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))",
      display: "flex",
      flexDirection: "column",
      background: ap.cardBackgroundSolid,
      backdropFilter: "none",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    },
  },

  paperInner: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      justifyContent: "center",
      width: "100%",
      boxSizing: "border-box",
    },
  },

  authForm: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      flex: "0 0 auto",
      width: "100%",
    },
  },

  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },

  logo: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,0.08)",
    background: ap.inputBackground,
    objectFit: "cover",
  },

  companyName: {
    fontWeight: 900,
    color: ap.textStrong,
    fontSize: 18,
    lineHeight: 1.2,
  },

  title: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontSize: 28,
    fontWeight: 900,
    color: ap.textStrong,
    letterSpacing: -0.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: 24,
      marginBottom: theme.spacing(1.5),
    },
  },

  field: {
    marginBottom: 14,
    "& .MuiOutlinedInput-root": {
      minHeight: 48,
      borderRadius: 12,
      "& fieldset": { borderColor: "rgba(15,23,42,0.14)" },
      "&:hover fieldset": { borderColor: "rgba(15,23,42,0.25)" },
      "&.Mui-focused fieldset": { borderColor: ap.accent },
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 12,
      "& .MuiOutlinedInput-root": {
        minHeight: 52,
      },
    },
  },

  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: 2,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.25),
    },
  },

  rememberMe: {
    color: ap.textMedium,
    marginRight: 0,
    "& .MuiFormControlLabel-label": { fontSize: 14 },
  },

  switch: {
    "& .MuiSwitch-switchBase.Mui-checked": { color: ap.accent },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: ap.accent },
  },

  registerLink: {
    color: ap.accent,
    textDecoration: "none",
    fontWeight: 800,
    "&:hover": { textDecoration: "underline" },
  },

  submit: {
    marginTop: 12,
    height: 48,
    borderRadius: 12,
    fontWeight: 900,
    letterSpacing: 0.4,
    background: ap.accentGradient,
    color: "#fff", // ✅ letras blancas
    "& .MuiButton-startIcon": { color: "#fff" }, // ✅ icono blanco
    [theme.breakpoints.down("sm")]: {
      minHeight: 52,
      height: 52,
      marginTop: theme.spacing(1),
    },
  },

  footerBox: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2.5),
      paddingTop: 0,
    },
  },

  /* =========================
     RIGHT: Abstract panel + bubbles (animado)
  ========================= */
  right: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "grid",
      placeItems: "center",
      padding: theme.spacing(6),
      position: "relative",
    },
  },

  rightCanvas: {
    width: "100%",
    maxWidth: 720,
    height: 520,
    borderRadius: 28,
    position: "relative",
    overflow: "hidden",
    border: "1px solid rgba(15,23,42,0.08)",
    background: ap.panelGlass,
    backdropFilter: "blur(10px)",
    boxShadow: ap.cardShadow,
  },

  blob1: {
    position: "absolute",
    width: 420,
    height: 420,
    left: -120,
    top: -140,
    borderRadius: "50%",
    background: "radial-gradient(circle at 30% 30%, rgba(37, 99, 235, 0.35), rgba(37, 99, 235, 0) 60%)",
    filter: "blur(6px)",
  },
  blob2: {
    position: "absolute",
    width: 460,
    height: 460,
    right: -180,
    bottom: -180,
    borderRadius: "50%",
    background: "radial-gradient(circle at 40% 40%, rgba(5, 150, 105, 0.28), rgba(5, 150, 105, 0) 62%)",
    filter: "blur(8px)",
  },
  blob3: {
    position: "absolute",
    width: 260,
    height: 260,
    right: 60,
    top: -80,
    borderRadius: "50%",
    background: "radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.22), rgba(99, 102, 241, 0) 65%)",
    filter: "blur(10px)",
  },

  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, rgba(15,23,42,0.05) 1px, transparent 1px), " +
      "linear-gradient(to bottom, rgba(15,23,42,0.05) 1px, transparent 1px)",
    backgroundSize: "42px 42px",
    opacity: 0.6,
    pointerEvents: "none",
  },

  badge: {
    position: "absolute",
    top: 22,
    left: 22,
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(15,23,42,0.06)",
    border: "1px solid rgba(15,23,42,0.08)",
    color: ap.textMedium,
    fontSize: 12,
    fontWeight: 800,
    backdropFilter: "blur(8px)",
  },

  bubble: {
    position: "absolute",
    padding: "12px 14px",
    borderRadius: 16,
    background: ap.panelGlass,
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 10px 30px rgba(15,23,42,0.10)",
    display: "grid",
    gap: 6,
    minWidth: 180,
    opacity: 0,
    transform: "translateY(14px) scale(0.98)",
    willChange: "opacity, transform",
    animationName: "$bubbleIn",
    animationDuration: "700ms",
    animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
    animationFillMode: "forwards",
  },

  bubble1: { animationDelay: "120ms" },
  bubble2: { animationDelay: "320ms" },
  bubble3: { animationDelay: "520ms" },

  bubbleTitle: {
    fontSize: 12,
    fontWeight: 900,
    color: ap.textMedium,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "rgba(37, 99, 235, 0.65)",
  },

  bubbleLine: {
    height: 10,
    borderRadius: 999,
    background: "rgba(15,23,42,0.08)",
  },

  "@keyframes bubbleIn": {
    from: { opacity: 0, transform: "translateY(14px) scale(0.98)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },

  "@media (prefers-reduced-motion: reduce)": {
    bubble: { animation: "none", opacity: 1, transform: "none" },
  },
  };
});

const Login = () => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { handleLogin } = useContext(AuthContext);
  const [viewregister, setviewregister] = useState("disabled");

  // ✅ tu hook original
  const { webInfo, loading: webInfoLoading, updatePageTitle } = useWebInfo();

  useEffect(() => {
    isMounted.current = true;

    if (webInfo?.title && isMounted.current) {
      updatePageTitle(webInfo.title);
    }

    return () => {
      isMounted.current = false;
    };
  }, [webInfo, updatePageTitle]);

  useEffect(() => {
    isMounted.current = true;

    // ✅ NUEVO: Verificar estado de registro usando endpoint público que verifica AMBAS configuraciones
    const fetchRegistrationStatus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await getRegistrationStatus({
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        if (!isMounted.current) return;

        // ✅ El registro está habilitado solo si AMBAS configuraciones están en "enabled"
        const registrationEnabled = response?.data?.enabled === true;
        setviewregister(registrationEnabled ? "enabled" : "disabled");
      } catch (error) {
        if (!isMounted.current) return;
        // ✅ Por defecto, deshabilitado si hay error o no existe
        setviewregister("disabled");
      }
    };

    fetchRegistrationStatus();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = async (e) => {
    e.preventDefault();

    // Intento con rememberMe opcional; si AuthContext no lo soporta, fallback
    try {
      await handleLogin(user, { rememberMe });
    } catch (err) {
      handleLogin(user);
    }
  };

  const backendUrl = window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // ✅ para evitar “parpadeo” por random en cada render: se calcula 1 vez
  const [logoWithRandom] = useState(() => `${backendUrl}/public/logotipos/login.png?r=${Date.now()}`);

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* IZQUIERDA */}
      <div className={classes.left}>
        <Paper className={classes.formPaper}>
          <div className={classes.paperInner}>
          <div className={classes.brandRow}>
            <img src={logoWithRandom} alt="logo" className={classes.logo} />
            <div className={classes.companyName}>
              {webInfoLoading ? "Cargando..." : webInfo?.title || "Watoolx"}
            </div>
          </div>

          <Typography className={classes.title}>Iniciar sesión</Typography>

          <form noValidate onSubmit={handlSubmit} className={classes.authForm}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.rowBetween}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className={classes.switch}
                  />
                }
                label="Recuérdame"
                className={classes.rememberMe}
              />
            </div>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
              startIcon={<ExitToAppIcon />}
            >
              ACCEDER
            </Button>

            {viewregister === "enabled" && (
              <Box mt={2} textAlign="center">
                <Typography variant="body2" style={{ color: "rgba(15,23,42,0.7)" }}>
                  ¿No tienes una cuenta?{" "}
                  <Link component={RouterLink} to="/signup" className={classes.registerLink}>
                    Regístrate
                  </Link>
                </Typography>
              </Box>
            )}

            <Box className={classes.footerBox}>
              <Copyright />
            </Box>
          </form>
          </div>
        </Paper>
      </div>

      {/* DERECHA */}
      <div className={classes.right}>
        <div className={classes.rightCanvas}>
          <div className={classes.blob1} />
          <div className={classes.blob2} />
          <div className={classes.blob3} />
          <div className={classes.gridOverlay} />

          <div className={classes.badge}>Panel</div>

          <div className={`${classes.bubble} ${classes.bubble1}`} style={{ top: 90, left: 70 }}>
            <div className={classes.bubbleTitle}>
              Chats <span className={classes.dot} />
            </div>
            <div className={classes.bubbleLine} style={{ width: 220 }} />
            <div className={classes.bubbleLine} style={{ width: 160 }} />
            <div className={classes.bubbleLine} style={{ width: 190 }} />
          </div>

          <div className={`${classes.bubble} ${classes.bubble2}`} style={{ top: 190, right: 80, minWidth: 230 }}>
            <div className={classes.bubbleTitle}>
              Multi Agente <span className={classes.dot} />
            </div>
            <div className={classes.bubbleLine} style={{ width: 240 }} />
            <div className={classes.bubbleLine} style={{ width: 170 }} />
            <div className={classes.bubbleLine} style={{ width: 210 }} />
          </div>

          <div className={`${classes.bubble} ${classes.bubble3}`} style={{ bottom: 110, left: 110, minWidth: 250 }}>
            <div className={classes.bubbleTitle}>
              Automatizaciones <span className={classes.dot} />
            </div>
            <div className={classes.bubbleLine} style={{ width: 250 }} />
            <div className={classes.bubbleLine} style={{ width: 180 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
